import { action, observable } from 'mobx';
import { KnowledgeBase } from 'models/entities/KnowledgeBase';
import { KnowledgeBaseStatus } from 'enums/KnowledgeBaseStatus';
import { ApiService } from 'services/ApiService';
import { ModelItem } from 'models/ModelItem';
import { PaginatedModelList } from 'models/PaginatedModelList';
import ApiRoutes from 'routes/api/ApiRoutes';
import Store from './Store';

export interface KnowledgeBaseFilters {
  page?: number;
  per_page?: number;
  search_query?: string;
  search?: string; // Frontend uses 'search', API uses 'search_query'
  status?: KnowledgeBaseStatus;
  tags?: string[];
}

export class KnowledgeBaseStore extends Store<KnowledgeBase> {
  @observable knowledgeBaseItem: ModelItem<KnowledgeBase> = new ModelItem<
    KnowledgeBase
  >(KnowledgeBase);

  @observable knowledgeBasePaginatedList: PaginatedModelList<
    KnowledgeBase
  > = new PaginatedModelList<KnowledgeBase>(KnowledgeBase);

  constructor() {
    super();
    KnowledgeBase._store = this;
  }

  @action
  setKnowledgeBaseItem = (item: KnowledgeBase): void => {
    this.knowledgeBaseItem.setItem(item);
  };

  @action
  setKnowledgeBasePaginatedList = (
    list: PaginatedModelList<KnowledgeBase>,
  ): void => {
    this.knowledgeBasePaginatedList = list;
  };

  @action
  fetchKnowledgeBases = async (
    filters: KnowledgeBaseFilters = {},
  ): Promise<any> => {
    const params: { [key: string]: any } = {};

    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.tags && filters.tags.length > 0) {
      params.tags = filters.tags;
    }

    return this.knowledgeBasePaginatedList.load(
      ApiRoutes.knowledgeBase.list,
      params,
    );
  };

  @action
  fetchKnowledgeBaseById = async (id: number): Promise<any> => {
    try {
      this.knowledgeBaseItem.setLoading(true);

      const response = await ApiService.getInstance().get(
        ApiRoutes.knowledgeBase.show(id),
      );

      const knowledgeBase = KnowledgeBase.fromJson(response) as KnowledgeBase;
      this.knowledgeBaseItem.setItem(knowledgeBase);
      this.knowledgeBaseItem.setLoading(false);

      return response;
    } catch (error) {
      this.knowledgeBaseItem.setLoading(false);
      throw error;
    }
  };

  @action
  createKnowledgeBase = async (data: Partial<KnowledgeBase>): Promise<any> => {
    const response = await ApiService.getInstance().post(
      ApiRoutes.knowledgeBase.create,
      data,
    );

    const knowledgeBase = KnowledgeBase.fromJson(
      response.data,
    ) as KnowledgeBase;

    // Add to the paginated list
    this.knowledgeBasePaginatedList.appendItems([knowledgeBase]);

    return response;
  };

  @action
  deleteKnowledgeBase = async (id: number): Promise<boolean> => {
    await ApiService.getInstance().delete(ApiRoutes.knowledgeBase.delete(id));

    // Remove from the paginated list
    const existingItem = this.knowledgeBasePaginatedList.items.find(
      (item) => item.id === id,
    );
    if (existingItem) {
      existingItem.delete();
    }

    // Clear the item if it's the same as the current item
    if (this.knowledgeBaseItem.item?.id === id) {
      this.knowledgeBaseItem.setItem(null);
    }

    return true;
  };

  getUniqueTags = (): string[] => {
    const allTags = this.knowledgeBasePaginatedList.items.flatMap(
      (item) => item.tags || [],
    );
    return [...new Set(allTags)];
  };

  // Getter for items
  get items(): KnowledgeBase[] {
    return this.knowledgeBasePaginatedList.items;
  }

  // Getter for loading state
  get isLoading(): boolean {
    return this.knowledgeBasePaginatedList.loading;
  }

  // Getter for current item
  get currentItem(): KnowledgeBase | null {
    return this.knowledgeBaseItem.item;
  }

  // Getter for current item loading state
  get isCurrentItemLoading(): boolean {
    return this.knowledgeBaseItem.loading;
  }
}
